import React, { useEffect, useState, useRef } from "react";
import MelhorarSkills from "../components/MelhorarSkills";
import CampoPeriodo from "../components/CampoPeriodo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { obterMeuPerfil, salvarPerfil, enviarCurriculo, reportarFalhaCurriculo, baixarCurriculoGerado, skillsSoltas, AREAS_ATUACAO, SENIORIDADES, DISPONIBILIDADES, SKILLS_SUGERIDAS, listarUfs, listarMunicipios } from "../services/api";
import PreferenciasContato from "../components/PreferenciasContato";
import { rotulo } from "../utils/rotulos";

// Mesmo teto do servidor (PerfilEndpoints.MaxCvBytes).
const MAX_CV_MB = 10;
// NAVEGADOR DE DENTRO DE APP (LinkedIn, Instagram, Facebook). No Android ele muitas vezes nem abre o seletor de
// arquivos, ou abre e o envio não sai — e a maior parte de quem chega vem pelo post do LinkedIn (15/09/2026).
const NAVEGADOR_INTERNO = typeof navigator !== "undefined"
  && /LinkedInApp|FBAN|FBAV|Instagram|Line\/|TikTok/i.test(navigator.userAgent || "");

const vazio = {
  headline: "", resumo: "", areaAtuacao: "", senioridade: "", cidade: "", linkedinUrl: "", disponibilidade: 0, pcd: false,
  escolaridade: "", turnosAceitos: [], cnh: "", veiculoProprio: false, aceitaMudarDeCidade: false,
  experiencias: [], formacoes: [], skills: [], certificados: [],
  modalidadesAceitas: [], contratacoesAceitas: [],
  pretensaoClt: "", pretensaoPj: "",
};
// Os campos de pretensão vêm do input como texto; o backend espera número ou null.
// Vazio (ou lixo digitado) vira null = "não informado", nunca 0.
const numero = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
};
const paraSalvar = (x) => ({ ...x, pretensaoClt: numero(x.pretensaoClt), pretensaoPj: numero(x.pretensaoPj) });

// Escolaridade em texto livre no banco (40 caracteres); aqui vira lista fechada para não virar bagunça de grafia.
const ESCOLARIDADES = ["Fundamental incompleto", "Fundamental completo", "Médio incompleto", "Médio completo",
  "Técnico", "Superior incompleto", "Superior completo", "Pós-graduação", "Mestrado ou doutorado"];
const CNHS = ["A", "B", "AB", "C", "D", "E"];
// Mesmos quatro valores aceitos pelo servidor (PerfilEndpoints.FiltrarCsv): qualquer outro é descartado lá.
const TURNOS = ["Manhã", "Tarde", "Noite", "Fim de semana"];
const MODALIDADES_LOCAL = ["Remoto", "Hibrido", "Presencial"];
const CONTRATACOES = ["CLT", "PJ", "Freelance"];
const rotuloModal = rotulo;   // rótulo central — ver utils/rotulos.js

export default function CurriculoPage() {
  const { user } = useAuth();
  const [p, setP] = useState(vazio);
  const [temCv, setTemCv] = useState(false);
  const [cvNome, setCvNome] = useState(null);
  const [abrirSkills, setAbrirSkills] = useState(false);
  // Competência que está na lista e não aparece em nenhuma experiência (ver o bloco âmbar lá embaixo).
  // Relido depois de cada gravação, porque é exatamente aí que a pessoa mexeu no texto.
  const [soltas, setSoltas] = useState(null);
  const [skillInput, setSkillInput] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [enviandoCv, setEnviandoCv] = useState(false);
  // Erro do ENVIO fica no próprio bloco do upload: no topo da página, no celular, ele passava despercebido.
  const [erroCv, setErroCv] = useState(null);

  // 📍 CIDADES ONDE A PESSOA ACEITA TRABALHAR (16/09/2026). A lista antiga tinha 30 cidades escritas à mão e
  // ainda misturava "Remoto" e "Híbrido" como se fossem cidades: quem morava em Itanhaém não se achava e deixava
  // em branco. Agora vem do IBGE (5.571 municípios), escolhendo a UF primeiro — e dá para marcar mais de uma,
  // que é o caso de quem mora numa cidade e trabalha na vizinha.
  const [ufs, setUfs] = useState([]);
  const [uf, setUf] = useState("");
  const [cidadesUf, setCidadesUf] = useState([]);
  useEffect(() => { listarUfs().then(setUfs).catch(() => setUfs([])); }, []);
  useEffect(() => {
    if (!uf) { setCidadesUf([]); return; }
    listarMunicipios(uf).then(setCidadesUf).catch(() => setCidadesUf([]));
  }, [uf]);
  // O campo continua sendo UM texto ("São Paulo/SP ou Itanhaém/SP") — é o formato que o match já entende, dos dois
  // lados: vaga com duas praças e candidato com duas cidades.
  const cidadesEscolhidas = (p.cidade || "").split(/\s+ou\s+/i).map((c) => c.trim()).filter(Boolean);
  const gravarCidades = (lista) => setP({ ...p, cidade: lista.join(" ou ") });
  const adicionarCidade = (nome) => {
    if (!nome || !uf) return;
    const nova = `${nome}/${uf}`;
    if (!cidadesEscolhidas.includes(nova)) gravarCidades([...cidadesEscolhidas, nova]);
  };
  const removerCidade = (c) => gravarCidades(cidadesEscolhidas.filter((x) => x !== c));
  const [baixando, setBaixando] = useState(false);
  const [msg, setMsg] = useState(null);
  // A confirmação de salvo vive no TOPO da página. Quem clica no botão do RODAPÉ — o caminho natural de quem
  // acabou de preencher tudo — não vê nada acontecer e conclui que o site travou. Foi o relato literal de um
  // candidato em 21/08: "cliquei no botão e não aconteceu mais nada". Este estado leva a confirmação (e o
  // próximo passo) para junto do botão que a pessoa realmente clicou.
  const [salvo, setSalvo] = useState(false);
  const [erro, setErro] = useState(null);
  // Aviso de "salvou, mas a vaga ainda não aceita" — mostrado DENTRO do bloco de experiências, que é para onde a
  // tela rola nessa hora (no celular o topo já saiu de vista).
  const [aviso, setAviso] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?voltar= é a vaga de onde a pessoa veio ("Falta seu currículo para se candidatar"). Sem ele, quem subia o
  // PDF via "já salvei nos campos" e ia embora: SEIS pessoas em 08-10/09/2026 fizeram cadastro, verificação e
  // currículo em ~2 min e nenhuma voltou à vaga para concluir. Só caminho interno — nunca "//outro-site".
  const voltar = (() => { const v = searchParams.get("voltar") || ""; return v.startsWith("/") && !v.startsWith("//") ? v : null; })();

  useEffect(() => {
    if (!user) { setCarregando(false); return; }
    obterMeuPerfil()
      .then((d) => {
        setP({
          headline: d.headline || "", resumo: d.resumo || "", areaAtuacao: d.areaAtuacao || "",
          senioridade: d.senioridade || "", cidade: d.cidade || "", linkedinUrl: d.linkedinUrl || "", pcd: !!d.pcd,
          disponibilidade: d.disponibilidade ?? 0,
          escolaridade: d.escolaridade || "", turnosAceitos: d.turnosAceitos || [], cnh: d.cnh || "",
          veiculoProprio: !!d.veiculoProprio, aceitaMudarDeCidade: !!d.aceitaMudarDeCidade,
          experiencias: d.experiencias || [], formacoes: d.formacoes || [],
          skills: d.skills || [], certificados: d.certificados || [],
          modalidadesAceitas: d.modalidadesAceitas || [], contratacoesAceitas: d.contratacoesAceitas || [],
          pretensaoClt: d.pretensaoClt ?? "", pretensaoPj: d.pretensaoPj ?? "",
        });
        setTemCv(d.temCurriculo); setCvNome(d.curriculoNomeOriginal);
        skillsSoltas().then(setSoltas).catch(() => {});
      })
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [user]);

  // 💾 SALVA SOZINHO, 2 SEGUNDOS DEPOIS DE PARAR DE MEXER (16/09/2026). A Thainá subiu o currículo pelo celular e
  // precisou clicar nos TRÊS botões "Salvar" da página para ter certeza de que gravou: no celular eles ficam longe
  // um do outro e nada na tela dizia o que já estava salvo. Agora qualquer alteração é gravada sozinha e o estado
  // aparece ao lado do botão ("salvando…" / "salvo às 11:07"); o botão continua existindo para quem quer confirmar.
  const primeiraCarga = useRef(true);
  const ultimoSalvo = useRef("");
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState(null);
  const [falhaAuto, setFalhaAuto] = useState(false);
  // Diz "isto já está gravado no servidor" — usado depois de salvar à mão e depois da leitura do PDF, para o
  // autosave não repetir a mesma requisição.
  const marcarGravado = (x) => { ultimoSalvo.current = JSON.stringify(paraSalvar(x)); };

  useEffect(() => {
    if (carregando || !user) return undefined;
    // O primeiro `p` com a página já carregada é o que veio do servidor: nada a salvar.
    if (primeiraCarga.current) { primeiraCarga.current = false; ultimoSalvo.current = JSON.stringify(paraSalvar(p)); return undefined; }
    const agora = JSON.stringify(paraSalvar(p));
    if (agora === ultimoSalvo.current) return undefined;
    // A pausa evita uma requisição por tecla digitada: cada nova mudança cancela o envio anterior.
    const t = setTimeout(async () => {
      setSalvando(true);
      try {
        await salvarPerfil(JSON.parse(agora));
        ultimoSalvo.current = agora;
        setSalvoEm(new Date());
        setFalhaAuto(false);
      }
      catch { setFalhaAuto(true); }
      finally { setSalvando(false); }
    }, 2000);
    return () => clearTimeout(t);
  }, [p, carregando, user]);

  // O link LEVA A ORIGEM (state.from): sem ela o /entrar abria em "login" e, depois do cadastro, mandava para a
  // HOME — dois cadastros de 11/09/2026 ficaram rodando /curriculo › /entrar › / › /curriculo sem subir currículo.
  if (!user) {
    const origem = `/curriculo${voltar ? `?voltar=${encodeURIComponent(voltar)}` : ""}`;
    const para = { pathname: "/entrar" };
    const estado = { from: origem, motivo: "Crie sua conta (ou entre) para montar seu currículo — você volta direto para cá." };
    return <Tela><Aviso><Link to={para} state={estado} className="text-primary font-semibold">Crie sua conta ou entre</Link> para montar seu currículo.</Aviso></Tela>;
  }
  if (user.tipo === 1) return <Tela><Aviso>O currículo é uma área do candidato. Sua conta é de empresa.</Aviso></Tela>;
  if (carregando) return <Tela><p className="text-gray-500">Carregando...</p></Tela>;

  const set = (campo) => (e) => setP({ ...p, [campo]: e.target.value });

  const addItem = (lista, item) => setP({ ...p, [lista]: [...p[lista], item] });
  const updItem = (lista, i, campo, val) => setP({ ...p, [lista]: p[lista].map((x, j) => j === i ? { ...x, [campo]: val } : x) });
  const delItem = (lista, i) => setP({ ...p, [lista]: p[lista].filter((_, j) => j !== i) });
  const toggleArr = (campo, val) => setP((pp) => ({ ...pp, [campo]: pp[campo].includes(val) ? pp[campo].filter((x) => x !== val) : [...pp[campo], val] }));

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !p.skills.includes(s)) setP({ ...p, skills: [...p.skills, s] });
    setSkillInput("");
  };

  // A MESMA RÉGUA da vaga (MeCandidatar) e do servidor — que descarta experiência sem cargo e sem empresa.
  const prontoParaVaga = (x) => temCv
    || (x.skills?.length || 0) > 0
    || (x.experiencias || []).some((e) => (e.cargo || "").trim() || (e.empresa || "").trim());
  const pronto = prontoParaVaga(p);

  // Quem não tem PDF (mecânico, soldador, almoxarife — o público das vagas da Reframax) precisa achar o caminho
  // à mão. Abre uma experiência em branco, rola até ela e põe o cursor no cargo.
  const rolarParaExperiencia = () => setTimeout(() => {
    const el = document.getElementById("bloco-experiencias");
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    el?.querySelector("input")?.focus({ preventScroll: true });
  }, 60);
  const irParaExperiencia = () => {
    if (p.experiencias.length === 0) addItem("experiencias", { cargo: "", empresa: "", periodo: "", descricao: "" });
    rolarParaExperiencia();
  };
  // Tem o que a vaga e o match usam? (skill, ou experiência com cargo/empresa). Recebe o perfil explícito porque, logo
  // depois do upload, o `p` do closure ainda é o antigo.
  const temDadosUteis = (x) => (x.skills?.length || 0) > 0
    || (x.experiencias || []).some((e) => (e.cargo || "").trim() || (e.empresa || "").trim());
  const PDF_SEM_DADOS = "Guardei seu arquivo, mas não consegui tirar os dados dele (parece escaneado ou em imagem). Coloque aqui o seu último trabalho: cargo e empresa já bastam. Depois clique em Salvar.";

  // Falha em silêncio de propósito: é um aviso auxiliar, e um erro aqui não pode atrapalhar quem só
  // queria salvar o currículo.
  const recarregarSoltas = async () => {
    try { setSoltas(await skillsSoltas()); } catch { /* aviso é opcional */ }
  };

  const salvar = async () => {
    setMsg(null); setErro(null); setSalvo(false); setAviso(null); setSalvando(true);
    try {
      await salvarPerfil(paraSalvar(p));
      marcarGravado(p); setSalvoEm(new Date()); setFalhaAuto(false);
      recarregarSoltas();
      // Veio de uma vaga: salvar é o fim do desvio, não do caminho. Volta para lá, onde o botão de
      // candidatar agora aparece (o MeCandidatar relê o perfil ao montar).
      //
      // 🚨 MAS SÓ SE A VAGA FOR ACEITAR. Voltava sempre, e o currículo vazio era barrado de novo na vaga
      // ("Falta seu currículo"), que mandava de volta para cá: o Fabiano (Mecânico Montador, Reframax, 10/09/2026)
      // ficou 11 minutos nesse vaivém, 9 páginas, e desistiu. O Alequisson (Soldador) marcou "não consegui".
      if (voltar) {
        if (prontoParaVaga(p)) { navigate(voltar); return; }
        setAviso("Salvei o que você preencheu, mas para a vaga ainda falta o seu último trabalho. Não precisa de PDF: coloque o cargo e a empresa aqui e clique em Salvar de novo.");
        irParaExperiencia();
        return;
      }
      setMsg("Currículo salvo!");
      setSalvo(true);
    }
    catch (e) { setErro(e.message); setFalhaAuto(true); }
    finally { setSalvando(false); }
  };

  // Baixa o currículo em PDF (modelo do site). Salva antes, pra o PDF refletir o que está na tela.
  const baixarPdf = async (modelo) => {
    setMsg(null); setErro(null); setBaixando(true);
    try {
      await salvarPerfil(paraSalvar(p));
      marcarGravado(p); setSalvoEm(new Date()); setFalhaAuto(false);
      await baixarCurriculoGerado(modelo);
      setMsg(modelo === "ats"
        ? "PDF para sistemas de triagem (coluna única) gerado! Use quando o site da empresa pedir para anexar o currículo."
        : "Currículo em PDF gerado! Verifique seus downloads.");
    } catch (e) { setErro(e.message); }
    finally { setBaixando(false); }
  };

  const onCv = async (e) => {
    const f = e.target.files?.[0];
    const input = e.target;
    if (!f) return;
    setErro(null); setErroCv(null); setMsg(null); setAviso(null);
    const dadosArquivo = { nome: f.name, tamanho: f.size, tipo: f.type, navegadorInterno: NAVEGADOR_INTERNO };
    // Arquivo grande nem sai do celular: no 4G levaria minutos para, no fim, ser recusado pelo servidor.
    if (f.size > MAX_CV_MB * 1024 * 1024) {
      setErroCv(`Seu arquivo tem ${(f.size / 1024 / 1024).toFixed(1)} MB e o limite é ${MAX_CV_MB} MB. Costuma ser PDF escaneado: exporte o PDF direto do Word, Google Docs ou Canva, ou use "Não tenho PDF: preencher à mão".`);
      reportarFalhaCurriculo({ ...dadosArquivo, motivo: "grande (barrado no celular)" });
      if (input) input.value = "";
      return;
    }
    setEnviandoCv(true);
    try {
      const r = await enviarCurriculo(f);
      setTemCv(true); setCvNome(r.nome);
      const s = r.sugestoes;
      if (s) {
        // O PDF PREVALECE (você pediu pra ler o CV e preencher); campos não lidos mantêm o atual; skills são unidas.
        // E o resultado é GRAVADO na hora: quem sobe um currículo pedindo "leia e
        // preencha" já disse o que quer — exigir um segundo clique só fazia a pessoa perder tudo ao sair da
        // página. Aconteceu com candidatos reais: perfil ficou vazio e a empresa recebeu ficha em branco.
        const lido = (prev) => ({
          ...prev,
          headline: s.headline || prev.headline || "",
          // Releu o CV com IA? O que veio do CV MANDA, inclusive substituindo texto antigo. Manter o valor
          // anterior quando o novo vem vazio preservava leitura errada de uma versão anterior do extrator.
          resumo: s.viaIa ? (s.resumo || "") : (s.resumo || prev.resumo || ""),
          areaAtuacao: s.areaAtuacao || prev.areaAtuacao || "",
          senioridade: s.senioridade || prev.senioridade || "",
          cidade: s.cidade || prev.cidade || "",
          linkedinUrl: s.linkedinUrl || prev.linkedinUrl || "",
          skills: Array.from(new Set([...(s.skills || []), ...(prev.skills || [])])),
          experiencias: (s.experiencias && s.experiencias.length) ? s.experiencias : prev.experiencias,
          formacoes: (s.formacoes && s.formacoes.length) ? s.formacoes : prev.formacoes,
          certificados: (s.certificados && s.certificados.length) ? s.certificados : prev.certificados,
        });
        const novo = lido(p);
        setP(novo);
        let salvou = true;
        setSalvando(true);
        // 🔁 SEGUNDA TENTATIVA. A gravação de depois da leitura é a mais importante da página (é ela que evita o
        // "cliquei em Salvar três vezes") e é também a mais frágil: sai logo depois do upload, no mesmo 4G que
        // acabou de mandar o arquivo. Uma falha de rede aqui deixava o perfil vazio e a culpa caía na pessoa.
        try { await salvarPerfil(paraSalvar(novo)); }
        catch {
          await new Promise((r) => setTimeout(r, 1500));
          try { await salvarPerfil(paraSalvar(novo)); } catch { salvou = false; }
        }
        setSalvando(false);
        if (salvou) { marcarGravado(novo); setSalvoEm(new Date()); setFalhaAuto(false); }
        else setFalhaAuto(true);
        const achou = [s.areaAtuacao && "área", s.senioridade && "senioridade", s.cidade && "cidade",
          s.skills?.length && `${s.skills.length} skills`, s.experiencias?.length && `${s.experiencias.length} exp.`,
          s.certificados?.length && `${s.certificados.length} cert.`, s.linkedinUrl && "LinkedIn"].filter(Boolean).join(", ");
        const fonte = s.viaIa ? "🤖 Li seu currículo com IA (Claude)" : "📄 Li seu currículo";
        setMsg(salvou
          ? `${fonte} (${s.caracteresLidos} caracteres) e já salvei nos campos abaixo: ${achou || "pouca coisa"}. Confira e ajuste o que quiser: o que você mudar é salvo sozinho.`
          : `${fonte} (${s.caracteresLidos} caracteres) e preenchi os campos abaixo: ${achou || "pouca coisa"}. Não consegui salvar automaticamente — clique em Salvar para não perder.`);
        // 🚨 LIDO SEM NADA APROVEITÁVEL (PDF escaneado/imagem): a Maria (14/09/2026) subiu o PDF, leu "pouca coisa" no
        // topo e foi embora com o perfil vazio (0 skills, 0 experiências): fora da vaga e do match. O caminho à mão
        // aparece AQUI, no bloco de experiências, com a linha aberta e o cursor no cargo.
        if (!temDadosUteis(novo)) {
          if (!novo.experiencias.length) setP({ ...novo, experiencias: [{ cargo: "", empresa: "", periodo: "", descricao: "" }] });
          setAviso(PDF_SEM_DADOS);
          rolarParaExperiencia();
        }
      } else {
        setMsg("Currículo (PDF) enviado, mas não consegui ler o texto (pode ser um PDF de imagem/escaneado).");
        if (!temDadosUteis(p)) {
          if (!p.experiencias.length) setP({ ...p, experiencias: [{ cargo: "", empresa: "", periodo: "", descricao: "" }] });
          setAviso(PDF_SEM_DADOS);
          rolarParaExperiencia();
        }
      }
    }
    catch (err) {
      setErroCv(err.message + (NAVEGADOR_INTERNO ? " Se continuar falhando, abra o eaiRH no Chrome ou no Safari (menu ⋯ › Abrir no navegador)." : ""));
      reportarFalhaCurriculo({ ...dadosArquivo, motivo: err.message });
    }
    finally { setEnviandoCv(false); if (input) input.value = ""; }
  };

  // "salvando… / salvo às 11:07" — o mesmo elemento aparece ao lado de cada botão Salvar e na barra do celular.
  const hora = (d) => d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const indicador = salvando
    ? <span role="status" className="text-sm text-gray-500">salvando…</span>
    : falhaAuto
      ? <span role="alert" className="text-sm text-red-600 font-semibold">não consegui salvar — clique em Salvar</span>
      : salvoEm
        ? <span role="status" className="text-sm text-green-700">✓ salvo às {hora(salvoEm)}</span>
        : <span className="text-sm text-gray-400">salva sozinho</span>;

  // A borda/tamanho SEM largura: quem precisa de largura diferente de 100% (a UF, estreita) usa esta base. Somar
  // "w-24" ao inputCls não funciona — as duas classes mexem na mesma propriedade e o w-full do Tailwind vence,
  // que foi o que espremeu o seletor de cidade até virar só a setinha (print do Nelson, 16/09/2026).
  const campoCls = "rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none";
  const inputCls = `w-full ${campoCls}`;
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  return (
    <Tela>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl font-bold text-text">Meu currículo</h1>
        <div className="flex items-center gap-2">
          {/* 📄 DOIS MODELOS — e o padrão é o que não prejudica quem não escolheu (25/09/2026).

              O botão grande entrega o modelo de COLUNA ÚNICA, que os sistemas de triagem leem sem
              embaralhar. O layout antigo, de duas colunas, continua disponível no link ao lado porque o
              Nelson está certo sobre ele: *"seria interessante manter nosso PDF para ele baixar... o
              nosso ficou muito bom"* — currículo também vai para a mão de gente, e ali ele é melhor.

              O que não podia era o inverso: quem baixa sem saber que existe diferença levar o bonito e
              ser barrado na triagem por uma escolha de diagramação nossa. */}
          <div className="flex flex-col items-end">
            <button onClick={() => baixarPdf()} disabled={baixando} title="PDF formatado em duas colunas, no modelo do eaiRH"
                    className={`inline-flex items-center gap-2 rounded-xl bg-primary hover:bg-brand text-white font-semibold px-5 py-2 shadow-sm transition ${baixando ? "opacity-70 cursor-wait" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
              </svg>
              {baixando ? "Gerando PDF..." : "Baixar currículo em PDF"}
            </button>
            <button type="button" onClick={() => baixarPdf("ats")} disabled={baixando}
                    title="Coluna única, no formato que os sistemas de triagem (ATS) leem sem embaralhar. Use quando o site da empresa pedir o arquivo."
                    className="mt-1 text-[11px] text-gray-500 underline hover:text-gray-700">
              ou versão para sistemas de triagem (ATS)
            </button>
          </div>
          {/* No celular o Salvar daqui some: lá embaixo tem a barra fixa com um botão só. */}
          <span className="hidden md:inline">{indicador}</span>
          <button onClick={salvar} className="hidden md:block rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2">Salvar</button>
        </div>
      </div>
      <p className="mt-1 text-gray-600 text-sm">Esses dados alimentam o <strong>match com vagas</strong> (área, senioridade, cidade e skills).</p>
      {msg && <p role="status" className="mt-3 rounded-lg bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm">{msg}</p>}
      {erro && <p role="alert" className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">{erro}</p>}

      {/* Veio de uma vaga: o caminho de volta fica À VISTA desde o começo e muda de tom quando o currículo
          existe. É o que faltava para quem só sobe o PDF — esse nunca chegava ao "Falta um passo" do rodapé. */}
      {/* O botão SALVA antes de voltar: o link antigo voltava sem salvar, e a vaga relia o perfil ainda vazio. */}
      {voltar && (
        <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm text-gray-800">
            {pronto
              ? <><strong>Currículo pronto.</strong> Falta só concluir a candidatura na vaga, agora é um clique.</>
              : <>Você veio de uma vaga. <strong>Não precisa ter PDF:</strong> coloque o seu último trabalho (cargo e empresa) ou, se tiver o arquivo, suba aqui. Depois é só salvar para <strong>concluir a candidatura</strong>.</>}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {!pronto && (
              <button type="button" onClick={irParaExperiencia}
                className="rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2 text-sm">
                Não tenho PDF: preencher à mão
              </button>
            )}
            <button type="button" onClick={salvar}
              className={`hidden md:block rounded-lg font-semibold px-4 py-2 text-sm ${pronto ? "bg-primary hover:bg-brand text-white" : "border border-primary text-primary hover:bg-primary/5"}`}>
              Salvar e voltar para a vaga →
            </button>
          </div>
        </div>
      )}

      {/* Upload em destaque no topo: a leitura do PDF preenche o resto */}
      <section className="mt-5 rounded-2xl border-2 border-dashed border-primary bg-primary/5 p-6 text-center">
        <div className="text-3xl">📄</div>
        <h2 className="font-bold text-gray-900 mt-1">Tem seu currículo em PDF ou Word? Comece por aqui</h2>
        <p className="text-sm text-gray-600 mt-1">
          A gente <strong>lê o arquivo e preenche os campos abaixo</strong> automaticamente (inclusive o modelo do LinkedIn). Depois é só revisar e salvar.
        </p>
        <label className={`mt-3 inline-block ${enviandoCv ? "pointer-events-none" : ""}`}>
          <input type="file" accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onCv} className="hidden" disabled={enviandoCv} />
          <span className={`inline-flex items-center gap-2 rounded-xl text-white font-semibold px-5 py-2.5 transition ${enviandoCv ? "bg-gray-400 cursor-wait" : "cursor-pointer bg-primary hover:bg-brand"}`}>
            {enviandoCv ? "⏳ Lendo seu currículo..." : "⬆ Enviar PDF ou Word e preencher"}
          </span>
        </label>
        {enviandoCv && (
          <div className="mt-3 mx-auto max-w-xs h-1.5 rounded-full bg-gray-200 barra-indeterminada"
               role="progressbar" aria-label="Lendo o currículo">
            <span className="bg-primary" />
          </div>
        )}
        {erroCv && !enviandoCv && (
          <p role="alert" className="mt-3 mx-auto max-w-md rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm text-left">{erroCv}</p>
        )}
        {NAVEGADOR_INTERNO && (
          <p className="mt-3 mx-auto max-w-md rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-xs text-left">
            📱 Você está no navegador do aplicativo (LinkedIn, Instagram…). Se o botão não abrir seus arquivos, toque em <b>⋯</b> e escolha <b>Abrir no navegador</b> (Chrome ou Safari).
          </p>
        )}
        {temCv && <p className="text-xs text-gray-500 mt-2">Arquivo atual: {cvNome}</p>}
        <p className="text-[11px] text-gray-400 mt-1">Aceita PDF ou Word (.docx), até {MAX_CV_MB} MB. Extraímos os campos do seu currículo para você revisar antes de salvar.</p>
      </section>

      {/* Identidade */}
      <Bloco titulo="Resumo profissional">
        <div className="space-y-3">
          <div><label className={labelCls}>Headline</label>
            <input className={inputCls} aria-label="Headline do currículo" value={p.headline} onChange={set("headline")} placeholder="Ex: Desenvolvedora Front-end | React" /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className={labelCls}>Área de atuação</label>
              <select className={inputCls} aria-label="Área de atuação" value={p.areaAtuacao} onChange={set("areaAtuacao")}>
                <option value="">Selecione</option>
                {AREAS_ATUACAO.map((a) => <option key={a} value={a}>{a}</option>)}
              </select></div>
            <div><label className={labelCls}>Senioridade</label>
              <select className={inputCls} aria-label="Senioridade" value={p.senioridade} onChange={set("senioridade")}>
                <option value="">Selecione</option>
                {SENIORIDADES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
          </div>
          {/* Linha inteira só para as cidades: na grade de três colunas a caixa da cidade virava uma setinha
              espremida ao lado da UF (print do Nelson, 16/09/2026) e ninguém entendia o que clicar. */}
          <div><label className={labelCls}>Cidades onde aceito trabalhar</label>
              <div className="flex gap-2">
                <select className={`${campoCls} w-24 shrink-0`} aria-label="UF" value={uf}
                        onChange={(e) => setUf(e.target.value)}>
                  <option value="">UF</option>
                  {ufs.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <select className={`${campoCls} flex-1 min-w-0`} aria-label="Cidade" value="" disabled={!uf}
                        onChange={(e) => adicionarCidade(e.target.value)}>
                  <option value="">{uf ? "Escolha a cidade" : "Escolha a UF primeiro"}</option>
                  {cidadesUf.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {cidadesEscolhidas.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {cidadesEscolhidas.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-semibold px-2 py-1">
                      {c}
                      <button type="button" aria-label={`Remover ${c}`} onClick={() => removerCidade(c)} className="hover:text-red-600">×</button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-1">Pode marcar mais de uma. Sem cidade, vaga presencial perto de você não aparece.</p></div>
          <div><label className={labelCls}>Disponibilidade no mercado</label>
            <select className={inputCls} aria-label="Disponibilidade" value={p.disponibilidade} onChange={(e) => setP({ ...p, disponibilidade: Number(e.target.value) })}>
              {DISPONIBILIDADES.map((d) => <option key={d.v} value={d.v}>{d.t}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">Você controla isso. Empresas filtram por aqui — mas você nunca some sem querer.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Modalidade que aceito</label>
              <div className="flex flex-wrap gap-3">
                {MODALIDADES_LOCAL.map((m) => (
                  <label key={m} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={p.modalidadesAceitas.includes(m)} onChange={() => toggleArr("modalidadesAceitas", m)} /> {rotuloModal(m)}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Tipo de contratação</label>
              <div className="flex flex-wrap gap-3">
                {CONTRATACOES.map((c) => (
                  <label key={c} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input type="checkbox" checked={p.contratacoesAceitas.includes(c)} onChange={() => toggleArr("contratacoesAceitas", c)} /> {c}
                  </label>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400 sm:col-span-2 -mt-1">Marque tudo que você aceita. Em branco = indiferente (aparece em todas as buscas).</p>
            {/* PCD: declaração do próprio candidato, opcional. Quem marca passa a receber as vagas exclusivas nas
                recomendações; quem não marca não as recebe — elas seguem visíveis no mural para todo mundo. */}
            <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
              <input type="checkbox" className="mt-0.5" checked={p.pcd} onChange={() => setP({ ...p, pcd: !p.pcd })} />
              <span>Sou <strong>PCD</strong> (pessoa com deficiência). Marcando, as vagas exclusivas para PCD entram nas suas recomendações; sem marcar, elas não são recomendadas para você.</span>
            </label>
          </div>

          {/* 🎓🚗 CAMPOS QUE A VAGA OPERACIONAL PEDE E O CURRÍCULO NÃO TINHA (16/09/2026). Escolaridade, turno,
              CNH e disposição de mudar são exatamente os filtros das vagas de produção, logística e vendas — o
              grosso do mural. TODOS OPCIONAIS: em branco significa "não informado" e ninguém é barrado por isso. */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-700">Mais sobre você <span className="font-normal text-gray-400">(opcional, ajuda no match)</span></p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Escolaridade</label>
                <select className={inputCls} aria-label="Escolaridade" value={p.escolaridade} onChange={set("escolaridade")}>
                  <option value="">Prefiro não informar</option>
                  {ESCOLARIDADES.map((x) => <option key={x} value={x}>{x}</option>)}
                </select></div>
              <div><label className={labelCls}>Carteira de motorista (CNH)</label>
                <select className={inputCls} aria-label="CNH" value={p.cnh} onChange={set("cnh")}>
                  <option value="">Não tenho / não informar</option>
                  {CNHS.map((x) => <option key={x} value={x}>Categoria {x}</option>)}
                </select>
                <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={p.veiculoProprio} onChange={() => setP({ ...p, veiculoProprio: !p.veiculoProprio })} />
                  Tenho veículo próprio
                </label></div>
            </div>
            <div className="mt-3">
              <label className={labelCls}>Turnos que aceito</label>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {TURNOS.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={p.turnosAceitos.includes(t)} onChange={() => toggleArr("turnosAceitos", t)} />
                    {t}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Em branco = indiferente (aparece em todas as buscas).</p>
            </div>
            <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-0.5" checked={p.aceitaMudarDeCidade} onChange={() => setP({ ...p, aceitaMudarDeCidade: !p.aceitaMudarDeCidade })} />
              <span>Aceito <strong>mudar de cidade</strong> por uma boa oportunidade. Marcando, vagas presenciais fora da sua região continuam aparecendo nas recomendações.</span>
            </label>
          </div>

          {/* Pretensão salarial: opcional e separada por regime — o valor CLT e o PJ não se comparam.
              Em branco, a empresa recebe "não informado" (ninguém é barrado por não preencher). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Pretensão salarial CLT <span className="font-normal text-gray-400">(opcional)</span></label>
              <input className={inputCls} type="number" min="0" step="100" inputMode="decimal"
                value={p.pretensaoClt ?? ""} onChange={(e) => setP({ ...p, pretensaoClt: e.target.value })} placeholder="R$ por mês" />
            </div>
            <div>
              <label className={labelCls}>Pretensão salarial PJ <span className="font-normal text-gray-400">(opcional)</span></label>
              <input className={inputCls} type="number" min="0" step="100" inputMode="decimal"
                value={p.pretensaoPj ?? ""} onChange={(e) => setP({ ...p, pretensaoPj: e.target.value })} placeholder="R$ por mês" />
            </div>
            <p className="text-xs text-gray-400 sm:col-span-2 -mt-1">
              Só a empresa a que você se candidatar vê esse valor — ele nunca aparece publicamente. Deixando em branco, ela recebe "não informado".
            </p>
          </div>
          <div><label className={labelCls}>Resumo</label>
            <AutoTextarea className={inputCls} aria-label="Resumo profissional" minH={90} value={p.resumo} onChange={set("resumo")} placeholder="Conte um pouco da sua trajetória..." /></div>
          <div><label className={labelCls}>LinkedIn (URL)</label>
            <input className={inputCls} aria-label="URL do LinkedIn" value={p.linkedinUrl} onChange={set("linkedinUrl")} placeholder="https://linkedin.com/in/voce" /></div>
        </div>
      </Bloco>

      {/* Experiências */}
      <Bloco id="bloco-experiencias" titulo="Experiências" onAdd={() => addItem("experiencias", { cargo: "", empresa: "", periodo: "", descricao: "" })}>
        {aviso && <p role="alert" className="mb-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 text-sm">{aviso}</p>}
        {p.experiencias.length === 0 && <Vazio>Nenhuma experiência adicionada. Clique em "+ Adicionar" e coloque o seu último trabalho: cargo e empresa já bastam.</Vazio>}
        {p.experiencias.map((x, i) => (
          <LinhaItem key={i} onDel={() => delItem("experiencias", i)}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input className={inputCls} aria-label="Cargo" placeholder="Cargo" value={x.cargo} onChange={(e) => updItem("experiencias", i, "cargo", e.target.value)} />
              <input className={inputCls} aria-label="Empresa" placeholder="Empresa" value={x.empresa} onChange={(e) => updItem("experiencias", i, "empresa", e.target.value)} />
              <CampoPeriodo valor={x.periodo} onChange={(v) => updItem("experiencias", i, "periodo", v)} />
            </div>
            <AutoTextarea className={`${inputCls} mt-2`} aria-label="Descrição da experiência" minH={64} placeholder="O que você fazia..." value={x.descricao || ""} onChange={(e) => updItem("experiencias", i, "descricao", e.target.value)} />
          </LinhaItem>
        ))}
      </Bloco>

      {/* 🎯 SKILLS LOGO DEPOIS DA EXPERIÊNCIA (25/09/2026).

          Estavam no fim da página, depois de Formação — e o Nelson notou: *"as skills estão no final de
          tudo"*. É o campo que mais pesa nos dois lados: vale 55% do nosso match e é um dos dois campos
          que a ordenação dos ATS declara ler. O que decide mais estava no lugar menos visto.

          A mesma razão mudou a ordem do PDF gerado (ver CurriculoPdfGenerator). */}
      {/* Skills */}
      <Bloco titulo="Skills">
        <div className="flex gap-2">
          <input className={inputCls} aria-label="Adicionar skill" list="dl-skills" value={skillInput} onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} placeholder="Digite ou escolha uma skill e aperte Enter (ex: Atendimento ao cliente)" />
          <datalist id="dl-skills">{SKILLS_SUGERIDAS.map((s) => <option key={s} value={s} />)}</datalist>
          <button onClick={addSkill} className="px-4 rounded-lg bg-gray-700 text-white text-sm font-semibold">Adicionar</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {p.skills.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 text-sm rounded-full bg-blue-100 text-blue-800 px-3 py-1">
              {s} <button onClick={() => delItem("skills", i)} aria-label={`Remover skill ${s}`} className="text-blue-500 hover:text-blue-800">×</button>
            </span>
          ))}
        </div>

        {/* 🚫 AVISO DE "COMPETÊNCIA SOLTA" — REMOVIDO NO MESMO DIA EM QUE SUBIU (25/09/2026).

          Ficava aqui um bloco âmbar dizendo "N skills suas não aparecem em nenhuma experiência", vindo
          de /perfil/skills-soltas. A ideia veio de um post sobre como os ATS ordenam candidatura, e o
          número da nossa base parecia sustentar (62% das competências declaradas não aparecem escritas
          em nenhuma descrição).

          O Nelson viu na própria tela dele e matou: *"não aparece em experiência porque não dá para
          descrever tudo, mas conta os certificados e cursos, isso está totalmente errado"*. E está certo
          — o perfil dele tem 25 skills técnicas (C#, ABAP, UiPath, Selenium…); exigir que cada uma
          apareça na prosa das experiências é exigir um currículo que ninguém escreve. Pior: a conta
          ignorava certificado e formação, que são prova melhor que uma frase.

          A lição não é "a medida estava errada", é que o número media outra coisa: quantas skills não
          estão repetidas em texto — não quantas estão mal comprovadas. Fica a rota no servidor, sem uso
          na tela, e fica o registro para ninguém reconstruir isto achando que é novidade. */}

      {/* 🎯 O QUE FALTA, E ONDE APRENDER (25/09/2026). Antes desta linha o portal apontava o buraco
            ("faltou Excel para esta vaga") e não oferecia saída. O botão calcula quais skills abririam
            MAIS VAGAS DO PERFIL DESTA PESSOA e traz os cursos — gratuitos primeiro. */}
        <button onClick={() => setAbrirSkills(true)}
          className="mt-4 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white shadow hover:bg-orange-700">
          Melhorar minhas skills
        </button>
        <p className="mt-2 text-xs text-gray-500">
          Vemos quais skills mais aparecem nas vagas do seu perfil e onde aprender cada uma — com cursos gratuitos primeiro.
        </p>
      </Bloco>

      {/* Formação */}
      <Bloco titulo="Formação" onAdd={() => addItem("formacoes", { curso: "", instituicao: "", periodo: "" })}>
        {p.formacoes.length === 0 && <Vazio>Nenhuma formação adicionada.</Vazio>}
        {p.formacoes.map((x, i) => (
          <LinhaItem key={i} onDel={() => delItem("formacoes", i)}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input className={inputCls} aria-label="Curso" placeholder="Curso" value={x.curso} onChange={(e) => updItem("formacoes", i, "curso", e.target.value)} />
              <input className={inputCls} aria-label="Instituição" placeholder="Instituição" value={x.instituicao} onChange={(e) => updItem("formacoes", i, "instituicao", e.target.value)} />
              <CampoPeriodo valor={x.periodo} onChange={(v) => updItem("formacoes", i, "periodo", v)} comAtual={false} rotulo="Período da formação" />
            </div>
          </LinhaItem>
        ))}
      </Bloco>

      {/* Certificados */}
      <Bloco titulo="Certificados" onAdd={() => addItem("certificados", { nome: "", emissor: "", ano: "" })}>
        {p.certificados.length === 0 && <Vazio>Nenhum certificado adicionado.</Vazio>}
        {p.certificados.map((x, i) => (
          <LinhaItem key={i} onDel={() => delItem("certificados", i)}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input className={inputCls} aria-label="Nome do certificado" placeholder="Certificado" value={x.nome} onChange={(e) => updItem("certificados", i, "nome", e.target.value)} />
              <input className={inputCls} aria-label="Emissor do certificado" placeholder="Emissor" value={x.emissor || ""} onChange={(e) => updItem("certificados", i, "emissor", e.target.value)} />
              <input className={inputCls} aria-label="Ano do certificado" placeholder="Ano" value={x.ano || ""} onChange={(e) => updItem("certificados", i, "ano", e.target.value)} />
            </div>
          </LinhaItem>
        ))}
      </Bloco>

      {/* O popup fica FORA dos blocos: ele cobre a tela inteira e precisa viver no nível do modal. */}
      <MelhorarSkills
        aberto={abrirSkills}
        onFechar={() => setAbrirSkills(false)}
        aoAdicionar={(r) => {
          // A skill volta para o estado local na hora — sem isso a pessoa fecha o popup e não vê o que
          // acabou de adicionar, e acha que não funcionou.
          setP((prev) => ({
            ...prev,
            skills: prev.skills.includes(r.skill) ? prev.skills : [...prev.skills, r.skill],
            certificados: r.curso
              ? [...(prev.certificados || []), { nome: r.curso, emissor: r.emissor || "", ano: r.ano || "" }]
              : prev.certificados,
          }));
        }}
      />

      <div className="mt-5"><PreferenciasContato /></div>

      <div className="mt-6">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={salvar} className="hidden md:block rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-2.5">Salvar</button>
          <span className="hidden md:inline">{indicador}</span>
          {salvo && <span role="status" className="text-green-700 font-semibold text-sm">✓ Currículo salvo!</span>}
          {erro && <span role="alert" className="text-red-600 text-sm">{erro}</span>}
        </div>

        {/* PRÓXIMO PASSO. Salvar o currículo NÃO candidata a ninguém, e quem chegou aqui vindo de uma vaga não
            tem como saber disso — o mesmo candidato de 21/08 perguntou "agora eu tenho que me candidatar ou já
            está pronto?". Sem esta linha, a pessoa monta o currículo inteiro e vai embora achando que aplicou. */}
        {salvo && (
          <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
            <p className="text-sm text-gray-800">
              <strong>Falta um passo:</strong> salvar o currículo ainda não é uma candidatura. Volte à vaga que
              você quer e clique em <strong>Candidatar-se</strong> — agora é um clique, porque seus dados já estão aqui.
            </p>
            <Link to="/vagas" className="mt-2 inline-block rounded-lg bg-primary hover:bg-brand text-white font-semibold px-4 py-2 text-sm">
              Ver vagas e me candidatar →
            </Link>
          </div>
        )}
      </div>

      {/* 📱 CELULAR: UM BOTÃO SÓ, FIXO NO RODAPÉ (16/09/2026). Antes eram três "Salvar" espalhados pela página —
          topo, bloco da vaga e rodapé — e quem subia o currículo pelo celular clicava nos três sem saber qual
          valia. Aqui o botão está sempre à mão e, ao lado dele, o que está acontecendo com o salvamento. */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3">
        <span className="min-w-0 truncate">{indicador}</span>
        <button onClick={salvar}
          className="shrink-0 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2.5">
          {voltar && pronto ? "Salvar e voltar →" : "Salvar"}
        </button>
      </div>
    </Tela>
  );
}

function Tela({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">{children}</main>
    </div>
  );
}
function Aviso({ children }) {
  return <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600">{children}</div>;
}
function Bloco({ titulo, onAdd, children, id }) {
  return (
    <section id={id} className="mt-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm scroll-mt-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-gray-900">{titulo}</h2>
        {onAdd && <button onClick={onAdd} className="text-sm font-semibold text-primary hover:underline">+ Adicionar</button>}
      </div>
      {children}
    </section>
  );
}
function LinhaItem({ children, onDel }) {
  return (
    <div className="mb-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
      {children}
      <div className="mt-3 flex justify-end">
        <button onClick={onDel}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 text-gray-600 hover:text-red-600 hover:border-red-300 hover:bg-red-50 text-sm font-medium px-3 py-1.5 transition">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          </svg>
          Remover
        </button>
      </div>
    </div>
  );
}
function Vazio({ children }) { return <p className="text-sm text-gray-400">{children}</p>; }

// Textarea que cresce sozinha para mostrar todo o conteúdo (sem scroll interno cortando o texto).
function AutoTextarea({ value, minH = 64, className = "", ...props }) {
  const ref = useRef(null);
  const ajustar = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.max(minH, el.scrollHeight) + "px";
  };
  useEffect(() => { ajustar(); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea
      ref={ref}
      value={value}
      onInput={ajustar}
      className={`${className} resize-none overflow-hidden`}
      style={{ minHeight: minH }}
      {...props}
    />
  );
}
